import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useLocation } from 'react-router-dom';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField } from '@shared/services/FormServices';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { ValueLabel } from '@features/ax/shared/ValueLabel';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { Package, Plus, CheckCircle2, Globe, RefreshCw, Clock } from 'lucide-react';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { useReplacementOptions, useCompleteReplacement } from '@features/mx/operations/hooks';
import { useToast } from '@shared/hooks/use-toast';
import { useGenerateMeterNumber, useServiceOrdersJobCard, useGetMasterMeters, useSearchSubmeters } from '@features/mx/resources/hook';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useGetResourceTypeList, useGetResourceSubtypeList, useGetUtilityServices, useChoices, useMeterManufacturers, useMeterModels, useMeterReasonCodesList, useMeterReadingStatusList } from "@features/mx/hooks";
import { useConfigMapJson } from '@shared/api/hooks';
import { useMeterReadingFormatDetail } from '@features/mx/settings/hooks';

interface ReplacementProps {
  operation?: any;
  isCompleted?: boolean;
}

interface WarehouseResource {
  id: string;
  code: string;
  name: string;
  qty: number;
  location: string;
}

export const Replacement: React.FC<ReplacementProps> = ({
  operation,
  isCompleted: propIsCompleted,
}) => {
  const { operationId } = useParams();
  const location = useLocation();
  const fromHistory = (location.state as any)?.from === 'history';
  const fromPending = (location.state as any)?.from === 'pending';

  const statusStr = (operation?.statusDisplay || operation?.status_display || operation?.statusName || operation?.status_name || '').toLowerCase();
  const statusVal = Number(operation?.status ?? operation?.status_code ?? 0);

  const isCompleted = propIsCompleted !== undefined ? propIsCompleted : fromHistory === true;

  if (isCompleted) {
    let detailData = operation?.detailData || operation?.detail_data;
    if (typeof detailData === 'string') {
      try {
        detailData = JSON.parse(detailData);
      } catch (e) {
        // ignore
      }
    }
    detailData = detailData || {};

    const resourceCode = detailData?.replacedResourceCode || detailData?.newResourceCode || operation?.resourceOverview?.resourceId || operation?.resourceOverview?.resource_id || operation?.resourceCard?.resourceId || 'NA';
    const resourceType = detailData?.resourceType || detailData?.resource_type || operation?.resourceOverview?.resourceTypeDisplay || operation?.resourceOverview?.resource_type_display || 'NA';
    const resourceSubtype = detailData?.resourceSubtype || detailData?.resource_subtype || operation?.resourceOverview?.resourceSubtypeDisplay || 'NA';
    const serialNo = detailData?.serialNo || detailData?.serial_no || operation?.resourceOverview?.serialNo || 'NA';

    return (
      <div className="space-y-5 pt-2">
        <div className="bg-white p-6 rounded-xl border border-gray-200 border-l-4 border-l-emerald-500 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-gray-500 tracking-wider">
              Replacement Record
            </h3>
          </div>

          <div className="bg-emerald-50/70 border border-emerald-100 rounded-lg p-3.5 space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-emerald-800 text-xs">
                {resourceCode}
              </span>
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none text-[11px] font-medium px-2 py-0.2">
                {resourceSubtype}
              </Badge>
            </div>
            <h4 className="text-sm font-semibold text-gray-900">
              {resourceType}
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-8 pt-1">
            <ValueLabel label="Resource Type" value={resourceType} />
            <ValueLabel label="Sub Type" value={resourceSubtype} />
            <ValueLabel label="Serial No" value={serialNo} />
          </div>
        </div>
      </div>
    );
  }
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sourceType, setSourceType] = useState<'existing' | 'new'>('existing');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResourceId, setSelectedResourceId] = useState('');

  const remoteUtilityId = useRemoteUtilityId();
  const { refetch: fetchGeneratedId, isFetching: isGenerating } = useGenerateMeterNumber(remoteUtilityId || "", false);
  const { data: replacementOptionsData, isLoading: isLoadingOptions } = useReplacementOptions(operationId);

  // 1. Reading Form
  const readingForm = useForm({
    defaultValues: {
      finalReadDate: '',
      finalReadValue: '',
    },
  });

  // 2. Installation, Read & Site Images Form
  const installationForm = useForm({
    defaultValues: {
      installationDate: '',
      serviceOrderRaised: '',
      linkedServiceOrder: '',
      readDate: '',
      currentRead: '',
      siteImages: [],
    },
  });

  // 3. New Resource Form
  const newResourceForm = useForm({
    defaultValues: {
      resourceId: '',
      serialNo: '',
      serialNumber: '',
      utilityService: '',
      resourceType: '',
      subType: '',
      classification: '',
      parent_meter: '',
      category: '',
      subCategory: '',
      manufacturer: '',
      model: '',
      linkedSubMeters: [],
      premise: '',
      unit: '',
      latitude: '',
      longitude: '',
    },
  });

  const [watchResourceType, setWatchResourceType] = useState<string>('');
  const [watchSubType, setWatchSubType] = useState<string>('');
  const [watchCategory, setWatchCategory] = useState<string>('');
  const [watchManufacturer, setWatchManufacturer] = useState<string>('');
  const [watchPremise, setWatchPremise] = useState<string>('');
  const [watchConditionStatus, setWatchConditionStatus] = useState<string>('');

  const currentResourceType = newResourceForm.watch('resourceType');
  React.useEffect(() => {
    setWatchResourceType(currentResourceType || '');
  }, [currentResourceType]);

  const currentSubType = newResourceForm.watch('subType');
  React.useEffect(() => {
    setWatchSubType(currentSubType || '');
  }, [currentSubType]);

  const currentCategory = newResourceForm.watch('category');
  React.useEffect(() => {
    setWatchCategory(currentCategory || '');
  }, [currentCategory]);

  const currentManufacturer = newResourceForm.watch('manufacturer');
  React.useEffect(() => {
    setWatchManufacturer(currentManufacturer || '');
  }, [currentManufacturer]);

  const currentPremise = newResourceForm.watch('premise');
  React.useEffect(() => {
    setWatchPremise(currentPremise || '');
  }, [currentPremise]);

  const currentConditionStatus = newResourceForm.watch('condition_status');
  const currentConditionStatusCamel = newResourceForm.watch('conditionStatus');
  React.useEffect(() => {
    setWatchConditionStatus(currentConditionStatus || currentConditionStatusCamel || '');
  }, [currentConditionStatus, currentConditionStatusCamel]);

  const { data: resourceTypeResponse } = useGetResourceTypeList(remoteUtilityId);
  const { data: subtypesResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    watchResourceType ? [watchResourceType] : undefined
  );
  const subtypesList = subtypesResponse?.result || [];

  const selectedSubTypeObj = React.useMemo(() => {
    return subtypesList.find((s: any) => s.code === watchSubType);
  }, [subtypesList, watchSubType]);

  const formatId = selectedSubTypeObj?.readFormat;

  const selectedTypeObj = React.useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(results) ? results.find((item: any) => item.code === watchResourceType) : null;
  }, [resourceTypeResponse, watchResourceType]);

  const captureReadsEnabled = selectedTypeObj?.capabilities?.captureReads === 1;

  const { data: formatDetail } = useMeterReadingFormatDetail(
    (captureReadsEnabled && formatId) ? String(formatId) : '',
    remoteUtilityId || '',
  );

  const { data: statusList } = useMeterReadingStatusList(remoteUtilityId);
  const { data: reasonCodes } = useMeterReasonCodesList(
    remoteUtilityId || '',
    undefined,
    undefined,
    undefined,
    watchConditionStatus
  );

  const conditionStatusOptions = React.useMemo(() => {
    const raw = statusList?.result || [];
    if (raw.length > 0) {
      return raw.map((item: any) => ({
        label: item.name,
        value: item.id,
      }));
    }
    return [];
  }, [statusList]);

  const conditionReasonOptions = React.useMemo(() => {
    const raw = reasonCodes?.result || [];
    if (raw.length > 0) {
      return raw.map((item: any) => ({
        label: item.reason,
        value: String(item.id),
      }));
    }
    return [];
  }, [reasonCodes]);

  const resourceTypeOptions = React.useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name,
        value: item.code,
      }))
      : [];
  }, [resourceTypeResponse]);

  const { data: utilityServicesData } = useGetUtilityServices(remoteUtilityId);

  const utilityServiceOptions = React.useMemo(() => {
    return Array.isArray(utilityServicesData)
      ? utilityServicesData
        .filter((item: any) => item.isActive)
        .map((item: any) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
  }, [utilityServicesData]);

  const { data: categoryConfig } = useConfigMapJson(
    'category_json',
    remoteUtilityId,
    'category'
  );

  const { data: subCategoryConfig } = useConfigMapJson(
    'category_json',
    remoteUtilityId,
    'sub_category',
    watchCategory || undefined
  );

  const categoryOptions = React.useMemo(() => {
    return Array.isArray(categoryConfig)
      ? categoryConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [categoryConfig]);

  const subCategoryOptions = React.useMemo(() => {
    return Array.isArray(subCategoryConfig)
      ? subCategoryConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [subCategoryConfig]);

  const { data: premiseConfig } = useConfigMapJson(
    'territory_json',
    remoteUtilityId,
    'premise'
  );

  const { data: unitConfig } = useConfigMapJson(
    'territory_json',
    remoteUtilityId,
    'unit',
    watchPremise || undefined
  );

  const premiseOptions = React.useMemo(() => {
    return Array.isArray(premiseConfig)
      ? premiseConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [premiseConfig]);

  const unitOptions = React.useMemo(() => {
    return Array.isArray(unitConfig)
      ? unitConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [unitConfig]);

  const { data: subMeters } = useSearchSubmeters(remoteUtilityId);

  const subMeterOptions = React.useMemo(() => {
    const results = subMeters?.results || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: `${item.meterNumber} (${item.deviceNo})`,
        value: item.id,
      }))
      : [];
  }, [subMeters]);

  const { data: classificationChoices } = useChoices({
    choice: 'MeterOwnership',
    remoteUtilityId,
  });

  const classificationOptions = React.useMemo(() => {
    return Array.isArray(classificationChoices)
      ? classificationChoices.map((item: any) => ({
        label: item.value,
        value: item.key,
      }))
      : [];
  }, [classificationChoices]);

  const { data: masterMeters } = useGetMasterMeters(remoteUtilityId);

  const masterResourceOptions = React.useMemo(() => {
    return Array.isArray(masterMeters)
      ? masterMeters.map((item: any) => ({
        label: `${item.meterNumber} (${item.deviceNo})`,
        value: item.id,
      }))
      : [];
  }, [masterMeters]);

  const { data: manufacturerResponse } = useMeterManufacturers({
    remote_utility_id: remoteUtilityId || '',
    is_pagination_required: false,
  });

  const manufacturerOptions = React.useMemo(() => {
    const results = manufacturerResponse?.results;
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
      : [];
  }, [manufacturerResponse]);

  const { data: modelsResponse } = useMeterModels(
    remoteUtilityId,
    watchManufacturer || undefined
  );

  const modelOptions = React.useMemo(() => {
    const results = modelsResponse?.results || modelsResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.meterModel,
        value: item.id,
      }))
      : [];
  }, [modelsResponse]);

  const watchLinkedServiceOrder = installationForm.watch('linkedServiceOrder');
  const { data: serviceOrdersJobCard } = useServiceOrdersJobCard(remoteUtilityId);

  const jobCardOptions = React.useMemo(() => {
    if (!serviceOrdersJobCard || !Array.isArray(serviceOrdersJobCard)) return [];
    return serviceOrdersJobCard.map((jobCard: any) => ({
      value: String(jobCard.jobCardId),
      label: jobCard.soName,
    }));
  }, [serviceOrdersJobCard]);

  const selectedSoCard = React.useMemo(() => {
    return serviceOrdersJobCard?.find((jc: any) => String(jc.jobCardId) === String(watchLinkedServiceOrder)) || null;
  }, [serviceOrdersJobCard, watchLinkedServiceOrder]);

  const { mutate: completeReplacementMutation, isPending: isCompleting } = useCompleteReplacement();
  const { toast } = useToast();

  const optionsList = useMemo(() => {
    const res = replacementOptionsData?.result || replacementOptionsData?.results || replacementOptionsData;
    return Array.isArray(res) ? res : [];
  }, [replacementOptionsData]);

  const readingFields: FormField[] = [
    {
      name: 'finalReadDate',
      label: 'Final Read Date',
      group: 'Replaced Meter Reading',
      groupBorderNeeded: false,
      type: 'datepicker',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      placeholder: 'dd-mm-yyyy',
    },
    {
      name: 'finalReadValue',
      label: 'Final Read Value',
      group: 'Replaced Meter Reading',
      type: 'text',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      placeholder: 'e.g. 9876.54',
    },
  ];

  const baseNewResourceFields: FormField[] = React.useMemo(() => [
    // --- Section 1: Resource Information ---
    {
      name: 'resourceId',
      label: 'Resource ID',
      group: 'Resource Information',
      groupHelperText: 'Select the resource type and sub type to configure this resource.',
      groupBorderNeeded: false,
      type: 'text',
      required: true,
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      placeholder: 'e.g. RES-0001',
      helperText: 'Unique identifier — format: RES-XXXX',
      customRender: (field, form, error) => (
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              {...form.register(field.name)}
              type="text"
              placeholder={field.placeholder}
              className={`${field.classes?.input} ${error ? 'border-red-500' : ''}`}
            />
          </div>
          <Button
            type="button"
            variant="default"
            className="h-12 border-gray-300"
            disabled={isGenerating || !remoteUtilityId}
            onClick={async () => {
              try {
                const res = await fetchGeneratedId();
                if (res.data && res.data.meterNumber) {
                  form.setValue(field.name, res.data.meterNumber, { shouldValidate: true, shouldDirty: true });
                  toast({ title: "Generated", description: `Resource ID generated: ${res.data.meterNumber}` });
                }
              } catch (error) {
                toast({ title: "Error", description: "Failed to generate Resource ID", variant: "destructive" });
              }
            }}
          >
            {isGenerating ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </div>
      )
    },
    {
      name: 'serialNo',
      label: 'Serial Number',
      group: 'Resource Information',
      groupBorderNeeded: false,
      type: 'text',
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      placeholder: 'e.g. SN-HTX-00101',
      helperText: 'Unique serial number printed on the physical device.',
    },
    {
      name: 'utilityService',
      label: 'Utility Service',
      group: 'Resource Information',
      groupBorderNeeded: false,
      type: 'select',
      required: true,
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      options: utilityServiceOptions,
      helperText: 'Select the utility service type.',
    },
    {
      name: 'resourceType',
      label: 'Resource Type',
      group: 'Resource Information',
      groupBorderNeeded: false,
      type: 'select',
      required: true,
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      options: resourceTypeOptions,
      helperText: 'Select the category of resource being added.',
    },
    {
      name: 'subType',
      label: 'Resource Sub Type',
      group: 'Resource Information',
      groupBorderNeeded: false,
      type: 'select',
      required: true,
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      options: subtypesList.map((item: any) => ({
        label: item.name,
        value: item.code,
      })),
      disabled: (formValues) => !formValues.resourceType,
      helperText: 'Sub type determines which fields are collected.',
    },
    {
      name: 'classification',
      label: 'Resource Classification',
      group: 'Resource Information',
      groupBorderNeeded: false,
      type: 'select',
      required: true,
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      options: classificationOptions,
      helperText: 'Define the role of this resource within the hierarchy.',
    },
    {
      name: 'parent_meter',
      type: 'select',
      label: 'Master Resource',
      placeholder: 'Select master resource',
      required: true,
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      group: 'Resource Information',
      groupBorderNeeded: false,
      options: masterResourceOptions,
      showWhen: (formValues) => formValues.classification === 2 || formValues.classification === '2',
      helperText: 'Select the master resource this sub resource belongs to.',
    },
    {
      name: 'category',
      type: 'select',
      label: 'Category',
      placeholder: 'Select category',
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      group: 'Resource Information',
      groupBorderNeeded: false,
      options: categoryOptions,
      helperText: 'Broad category this resource belongs to.',
    },
    {
      name: 'subCategory',
      type: 'select',
      label: 'Sub-Category',
      placeholder: 'select a category first',
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      group: 'Resource Information',
      groupBorderNeeded: false,
      options: subCategoryOptions,
      disabled: (formValues) => !formValues.category,
      helperText: 'Specific sub-category within the selected category.',
    },
    {
      name: 'manufacturer',
      label: 'Manufacturer',
      group: 'Resource Information',
      groupBorderNeeded: false,
      type: 'select',
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      options: manufacturerOptions,
      helperText: 'Company that manufactured this resource.',
    },
    {
      name: 'model',
      label: 'Model',
      group: 'Resource Information',
      groupBorderNeeded: false,
      type: 'select',
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      options: modelOptions,
      disabled: (formValues) => !formValues.manufacturer,
      helperText: 'Specific model of the resource.',
    },
    {
      name: 'linkedSubMeters',
      type: 'multi-select',
      label: 'Link Sub Resources',
      placeholder: 'Search sub resources...',
      classes: FIELD_CLASSES,
      className: 'col-span-2',
      group: 'Resource Information',
      groupBorderNeeded: false,
      options: subMeterOptions,
      showWhen: (formValues) => formValues.classification === 0 || formValues.classification === '0',
      helperText: 'Search and select existing sub resources to attach to this master.',
    },

    // --- Section 2: Location Details ---
    {
      name: 'locationDetailsLink',
      type: 'custom',
      className: 'col-span-2',
      group: 'Location Details',
      groupHelperText: 'Define where the resource is installed.',
      groupBorderNeeded: false,
      customRender: () => (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-4 py-3 rounded-lg flex items-center justify-between w-full mt-1 mb-3">
          <span>Can't find the premise or unit? Want to add a new one?</span>
          <a
            href="/utility/utility-setup/service-area"
            target="_blank"
            rel="noreferrer"
            className="font-bold underline hover:text-amber-950 transition-colors"
          >
            Go to Service Areas
          </a>
        </div>
      ),
    },
    {
      name: 'premise',
      type: 'select',
      label: 'Premise',
      placeholder: 'Select premise',
      required: true,
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      group: 'Location Details',
      groupBorderNeeded: false,
      options: premiseOptions,
      helperText: 'Select the premise location.',
    },
    {
      name: 'unit',
      type: 'select',
      label: 'Unit',
      placeholder: 'select a premise first',
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      group: 'Location Details',
      groupBorderNeeded: false,
      options: unitOptions,
      disabled: (formValues) => !formValues.premise,
      helperText: 'Select the unit at the selected premise.',
    },
    {
      name: 'latitude',
      type: 'text',
      label: 'Latitude',
      placeholder: 'e.g. 34.0522° N',
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      group: 'Location Details',
      groupBorderNeeded: false,
      helperText: 'Enter the latitude coordinates.',
    },
    {
      name: 'longitude',
      type: 'text',
      label: 'Longitude',
      placeholder: 'e.g. 118.2437° W',
      classes: FIELD_CLASSES,
      className: 'col-span-2 md:col-span-1',
      group: 'Location Details',
      groupBorderNeeded: false,
      helperText: 'Enter the longitude coordinates.',
    },
  ], [
    isGenerating,
    remoteUtilityId,
    fetchGeneratedId,
    toast,
    utilityServiceOptions,
    resourceTypeOptions,
    subtypesList,
    classificationOptions,
    masterResourceOptions,
    categoryOptions,
    subCategoryOptions,
    manufacturerOptions,
    modelOptions,
    subMeterOptions,
    premiseOptions,
    unitOptions,
  ]);

  // Dynamically resolve custom fields based on selected subType config
  const dynamicFields: FormField[] = React.useMemo(() => {
    if (!watchSubType) return [];

    const selected = subtypesList.find((s: any) => s.code === watchSubType);
    if (!selected) return [];

    const apiCustomFields = selected.customFields || [];
    if (Array.isArray(apiCustomFields) && apiCustomFields.length > 0) {
      return apiCustomFields.map((cf: any) => {
        const labelStr = cf.label || cf.fieldLabel || cf.field_label || '';
        const rawType = (cf.type || 'text').toLowerCase();
        let fieldType: FormField['type'] = 'text';
        if (rawType === 'radio' || rawType === 'yes/no') {
          fieldType = 'radio';
        } else if (rawType === 'switch') {
          fieldType = 'switch';
        } else if (rawType === 'dropdown' || rawType === 'select') {
          fieldType = 'select';
        }

        return {
          name: `${labelStr.replace(/\s+/g, '_').toLowerCase()}`,
          type: fieldType,
          label: labelStr,
          placeholder: cf.placeholder || `Enter ${labelStr.toLowerCase()}`,
          required: cf.required !== undefined
            ? cf.required
            : (cf.isRequired !== undefined
              ? cf.isRequired
              : (cf.is_required !== undefined ? cf.is_required : false)),
          classes: FIELD_CLASSES,
          className: 'col-span-2 md:col-span-1',
          group: 'Specifications',
          groupHelperText: 'Configure specification details for the selected sub type.',
          groupBorderNeeded: false,
          options: Array.isArray(cf.options)
            ? cf.options.map((o: any) => typeof o === 'string' ? { label: o, value: o } : o)
            : (fieldType === 'radio' ? [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }] : []),
        };
      });
    }

    return [];
  }, [watchSubType, subtypesList]);

  const readFormatFields: FormField[] = React.useMemo(() => {
    if (!captureReadsEnabled || !formatDetail) return [];

    const flatFields: FormField[] = [];
    if (formatDetail?.sections && Array.isArray(formatDetail.sections)) {
      const formatSection = formatDetail.sections.find(
        (sec: any) => sec.section === 'Format Fields'
      );

      if (formatSection?.fields && Array.isArray(formatSection.fields)) {
        formatSection.fields.forEach((f: any) => {
          if (f.isActive === false) return;

          const fieldTypeUpper = (f.fieldType || '').toUpperCase();
          let type: FormField['type'] = 'text';
          if (fieldTypeUpper === 'DATE') {
            type = 'datepicker';
          } else if (fieldTypeUpper === 'NUMERIC') {
            type = 'number';
          } else if (fieldTypeUpper === 'DROPDOWN') {
            type = 'select';
          } else if (fieldTypeUpper === 'PHOTO') {
            type = 'file';
          }

          const fieldNameVal = f.fieldName || f.label || '';
          const fieldKey = f.key || f.fieldName || '';

          const field: FormField = {
            name: fieldKey,
            label: fieldNameVal,
            type,
            required: f.required !== undefined ? f.required : (f.isRequired !== undefined ? f.isRequired : false),
            placeholder: `Enter ${fieldNameVal.toLowerCase()}`,
            classes: FIELD_CLASSES,
            className: 'col-span-2 md:col-span-1',
            group: 'Read Details',
            groupHelperText: 'Details required for recording resource readings.',
            groupBorderNeeded: false,
          };

          if (fieldTypeUpper === 'DROPDOWN') {
            if (fieldKey === 'condition_status' || fieldKey === 'conditionStatus') {
              field.options = conditionStatusOptions;
            } else if (fieldKey === 'condition_reason' || fieldKey === 'conditionReason') {
              field.options = conditionReasonOptions;
            } else {
              field.options = [];
            }
          }

          flatFields.push(field);
        });

        // Sort by sequenceId
        flatFields.sort((a, b) => {
          const fieldA = formatSection.fields.find((f: any) => (f.key || f.fieldName || f.field_name) === a.name);
          const fieldB = formatSection.fields.find((f: any) => (f.key || f.fieldName || f.field_name) === b.name);
          const seqA = fieldA?.sequenceId ?? 0;
          const seqB = fieldB?.sequenceId ?? 0;
          return seqA - seqB;
        });
      }
    }

    return flatFields;
  }, [formatDetail, captureReadsEnabled, conditionStatusOptions, conditionReasonOptions]);

  const newResourceFields = React.useMemo(() => {
    return [...baseNewResourceFields, ...dynamicFields, ...readFormatFields];
  }, [baseNewResourceFields, dynamicFields, readFormatFields]);

  const installationFields: FormField[] = [
    {
      name: 'installationDate',
      label: 'Installation Date',
      group: 'Installation Details',
      groupBorderNeeded: false,
      type: 'datepicker',
      required: true,
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      placeholder: 'dd-mm-yyyy',
    },
    {
      name: 'serviceOrderRaised',
      label: 'Service Order Raised',
      group: 'Installation Details',
      type: 'radio',
      className: 'col-span-1 mt-2',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ],
    },
    {
      name: "linkedServiceOrder",
      type: "select",
      options: jobCardOptions,
      label: "Search Service Order",
      placeholder: "Search service order",
      group: "Installation Details",
      groupHelperText: "Installation setup, site coordinates, and service order linkage.",
      groupBorderNeeded: false,
      showWhen: (formValues) => formValues.serviceOrderRaised === "yes",
      classes: FIELD_CLASSES,
      className: "col-span-1",
      helperText: "Search by service order ID, name, or assigned user",
    },
    {
      name: "so_details_card",
      type: "custom",
      dependsOn: "linkedServiceOrder",
      label: "Service Order Details",
      showWhen: (formValues: any) =>
        !!formValues.linkedServiceOrder && formValues.serviceOrderRaised === "yes",
      group: "Installation Details",
      groupBorderNeeded: false,
      className: "col-span-2",
      customRender: () => (
        <div className="p-4 border rounded-lg bg-slate-50 flex flex-col gap-2 w-full mt-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
            <div>
              <p className="text-xs text-slate-500 mb-1">SO ID</p>
              <p className="font-medium text-sm">{selectedSoCard?.soId ?? "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">SO Name</p>
              <p className="font-medium text-sm">{selectedSoCard?.soName ?? "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Completed On</p>
              <p className="font-medium text-sm">{selectedSoCard?.completedOn ?? "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Assigned To</p>
              <p className="font-medium text-sm">{selectedSoCard?.assignedTo ?? "N/A"}</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: 'readDate',
      label: 'Read Date',
      group: 'Read',
      groupBorderNeeded: false,
      type: 'datepicker',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      placeholder: 'dd-mm-yyyy',
    },
    {
      name: 'currentRead',
      label: 'Current Read',
      group: 'Read',
      type: 'text',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      placeholder: 'e.g. 1042.50',
    },
    {
      name: 'siteImages',
      label: '',
      group: 'Attach Site Images',
      groupBorderNeeded: false,
      type: 'multi-file',
      accept: 'image/png, image/jpeg, image/webp',
      multiple: true,
      className: 'col-span-2',
      placeholder: 'Click to upload or drag images here (JPG, PNG, WEBP)',
      showWhen: (values) => !!values.installationDate,
    },
  ];

  const columns: TableColumn<WarehouseResource>[] = [
    {
      key: 'resourceId',
      header: 'Resource ID',
      render: (val) => (
        <span className="font-semibold text-blue-600">{val}</span>
      ),
    },
    {
      key: 'warehouseName',
      header: 'Location',
      render: (val) => <span className="text-gray-600">{val}</span>,
    },
  ];

  const filteredWarehouseResources = (optionsList || []).filter((res: any) => {
    if (!res) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      res.resourceId?.toLowerCase().includes(q) ||
      res.warehouseName?.toLowerCase().includes(q) ||
      res.serialNo?.toLowerCase().includes(q)
    );
  });

  const selectedResource = useMemo(() => {
    return (optionsList || []).find((r: any) => String(r?.id) === selectedResourceId);
  }, [selectedResourceId, optionsList]);

  const handleMarkAsComplete = (data: any) => {
    const formData = new FormData();
    formData.append('source', sourceType);
    formData.append('final_read_date', data.finalReadDate || '');
    formData.append('final_read_value', data.finalReadValue || '');

    if (sourceType === 'existing') {
      if (selectedResourceId) {
        formData.append('warehouse_resource_id', selectedResourceId);
      }
    } else {
      formData.append('new_path', 'add_immediately');
      formData.append('resource_id', data.resourceId || '');
      formData.append('meter_number', data.resourceId || '');
      formData.append('serial_number', data.serialNo || data.serialNumber || '');
      formData.append('device_no', data.serialNo || data.serialNumber || '');
      formData.append('utility_service', data.utilityService || '');
      formData.append('product_code', data.utilityService || '');
      formData.append('resource_type_code', data.resourceType || data.type || '');
      formData.append('resource_sub_type_code', data.subType || '');
      formData.append('classification', data.classification || '');
      formData.append('ownership', data.classification || '');
      if (data.model) {
        formData.append('model_id', data.model);
        formData.append('meter_model', data.model);
      }
      if (data.manufacturer) {
        formData.append('meter_make', data.manufacturer);
      }
      if (data.category) {
        formData.append('category', data.category);
      }
      if (data.subCategory) {
        formData.append('sub_category', data.subCategory);
      }
      if (data.premise) {
        formData.append('premise', data.premise);
      }
      if (data.unit) {
        formData.append('unit_code', data.unit);
      }
      if (data.latitude) {
        formData.append('lat', String(data.latitude));
      }
      if (data.longitude) {
        formData.append('long', String(data.longitude));
      }
      if (data.parent_meter) {
        formData.append('parent_meter', String(data.parent_meter));
      }
      if (data.linkedSubMeters && Array.isArray(data.linkedSubMeters)) {
        data.linkedSubMeters.forEach((id: any) => {
          formData.append('linked_sub_meters', id);
        });
      }

      // Dynamic Specifications payload
      const dynamicPayload: any = {};
      dynamicFields.forEach((field) => {
        if (data[field.name] !== undefined) {
          dynamicPayload[field.name] = data[field.name];
        }
      });
      formData.append('additional_data', JSON.stringify({ resource_custom_fields: dynamicPayload }));

      // Read format fields
      if (captureReadsEnabled) {
        readFormatFields.forEach((field) => {
          if (data[field.name] !== undefined && data[field.name] !== '') {
            formData.append(field.name, data[field.name]);
          }
        });
      }
    }

    formData.append('installation_date', data.installationDate || '');
    formData.append('is_so_raised', data.serviceOrderRaised === "yes" ? "1" : "0");

    if (data.serviceOrderRaised === 'yes' && data.linkedServiceOrder) {
      formData.append('service_order_id', data.linkedServiceOrder);
    }

    formData.append('read_date', data.readDate || '');
    formData.append('current_read', data.currentRead || '');

    if (data.siteImages && Array.isArray(data.siteImages)) {
      data.siteImages.forEach((file: File) => {
        formData.append('site_images', file);
      });
    }

    if (operationId) {
      completeReplacementMutation({ operationId, payload: formData }, {
        onSuccess: () => {
          setIsSubmitted(true);
          toast({ title: 'Success', description: 'Replacement completed successfully.' });
        },
        onError: (err: any) => {
          const apiMsg = err?.response?.data?.message || err?.message || 'Failed to complete replacement.';
          toast({ title: 'Error', description: apiMsg, variant: 'destructive' });
        }
      });
    }
  };

  return (
    <div className="space-y-5 pt-2">
      {/* Top Resource Banner */}
      <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 font-medium">Replacing:</span>
          <span className="font-semibold text-gray-900">{operation?.resourceOverview?.resourceId || 'NA'}</span>
        </div>
        <Badge
          variant="outline"
          className="bg-gray-100 text-gray-700 border-gray-300 text-xs font-medium px-2.5 py-0.5 rounded-full"
        >
          {operation?.resourceOverview?.resourceTypeDisplay || 'NA'}
        </Badge>
      </div>

      {!isSubmitted ? (
        /* State 1: Active Editing & Completion Flow */
        <div className="space-y-5">
          {/* Section 1: Replaced Meter Reading */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <DynamicForm
              form={readingForm}
              fields={readingFields}
              onSubmit={() => { }}
              gridColumns={2}
              config={{ showSubmitButton: false, showResetButton: false }}
              renderCustomFooter={() => <></>}
            />
          </div>

          {/* Section 2: Select Replacement Resource */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
            {/* Step Header */}
            <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold shrink-0">
                2
              </span>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Select Replacement Resource
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Choose how the replacement will be sourced
                </p>
              </div>
            </div>

            {/* Sourcing Mode Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Card: Existing */}
              <div
                onClick={() => setSourceType('existing')}
                className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3.5 ${sourceType === 'existing'
                  ? 'border-blue-500 bg-blue-50/40 ring-1 ring-blue-500'
                  : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
              >
                <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Existing</h4>
                  <p className="text-xs text-gray-500 mt-0.5">From warehouse</p>
                </div>
              </div>

              {/* Card: New Resource */}
              <div
                onClick={() => setSourceType('new')}
                className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3.5 ${sourceType === 'new'
                  ? 'border-blue-500 bg-blue-50/40 ring-1 ring-blue-500'
                  : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
              >
                <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
                  <Plus className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">New Resource</h4>
                  <p className="text-xs text-gray-500 mt-0.5">Request new</p>
                </div>
              </div>
            </div>

            {/* Sourcing Content */}
            {sourceType === 'existing' ? (
              <div className="pt-2">
                <GenericTable
                  data={filteredWarehouseResources}
                  columns={columns}
                  rowKey="id"
                  selectable={true}
                  selectedRows={selectedResourceId ? [selectedResourceId] : []}
                  onSelectionChange={(keys) => setSelectedResourceId(keys[keys.length - 1] || '')}
                  searchPlaceholder="Search by ID, location..."
                  onSearch={(term) => setSearchQuery(term)}
                  emptyMessage="No warehouse resources found."
                />
              </div>
            ) : (
              <div className="pt-2">
                <DynamicForm
                  form={newResourceForm}
                  fields={newResourceFields}
                  onSubmit={() => { }}
                  gridColumns={2}
                  config={{ showSubmitButton: false, showResetButton: false }}
                  renderCustomFooter={() => <></>}
                />
              </div>
            )}
          </div>

          {/* Section 3: Installation Details, Read & Attach Site Images */}
          {(selectedResourceId || sourceType === 'new') && (
            <div className="space-y-5">
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
                <DynamicForm
                  form={installationForm}
                  fields={installationFields}
                  onSubmit={() => { }}
                  gridColumns={2}
                  config={{ showSubmitButton: false, showResetButton: false }}
                  renderCustomFooter={() => <></>}
                />

                {/* Full-width Complete Button */}
                {installationForm.watch('installationDate') && (
                  <Button
                    type="button"
                    disabled={isCompleting || (sourceType === 'existing' && !selectedResourceId)}
                    onClick={async () => {
                      if (sourceType === 'new') {
                        const isValid = await newResourceForm.trigger();
                        if (!isValid) {
                          toast({
                            title: "Validation Error",
                            description: "Please fill in all required New Resource details.",
                            variant: "destructive"
                          });
                          return;
                        }
                      }
                      installationForm.handleSubmit((data) => {
                        handleMarkAsComplete({
                          ...readingForm.getValues(),
                          ...newResourceForm.getValues(),
                          ...data
                        });
                      })();
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm rounded-xl py-6"
                  >
                    {isCompleting ? 'Completing...' : 'Mark as Complete'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* State 2: Post-Submission State */
        <div className="bg-white p-6 rounded-xl border border-gray-200 border-l-4 border-l-emerald-500 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-gray-500 tracking-wider">
              {sourceType === 'existing' ? 'Replacement — From Warehouse' : 'Replacement — New Resource'}
            </h3>
          </div>

          {/* Existing Resource Pill */}
          <div>
            <Badge
              variant="outline"
              className="bg-blue-50 text-blue-700 border-blue-200 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5 w-fit"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>{sourceType === 'existing' ? 'Existing Resource' : 'New Resource'}</span>
            </Badge>
          </div>

          {/* Green Highlight Box */}
          <div className="bg-emerald-50/70 border border-emerald-100 rounded-lg p-3.5 space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-emerald-800 text-xs">
                {sourceType === 'existing'
                  ? (selectedResource?.resourceId || selectedResource?.code || 'NA')
                  : (newResourceForm.getValues('resourceId') || 'NA')}
              </span>
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none text-[11px] font-medium px-2 py-0.2">
                {sourceType === 'existing'
                  ? (selectedResource?.subType || 'NA')
                  : (subtypesList.find((s: any) => s.code === newResourceForm.getValues('subType'))?.name || newResourceForm.getValues('subType') || 'NA')}
              </Badge>
            </div>
            <h4 className="text-sm font-semibold text-gray-900">
              {sourceType === 'existing'
                ? (selectedResource?.name || 'NA')
                : (manufacturerOptions.find((m: any) => m.value === newResourceForm.getValues('manufacturer'))?.label || newResourceForm.getValues('manufacturer') || 'NA')}
            </h4>
          </div>

          {/* Resource Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 pt-1">
            <ValueLabel
              label="Resource Type"
              value={sourceType === 'existing'
                ? (selectedResource?.type || 'NA')
                : (resourceTypeOptions.find((t: any) => t.value === newResourceForm.getValues('resourceType'))?.label || newResourceForm.getValues('resourceType') || 'NA')}
            />
            <ValueLabel
              label="Sub Type"
              value={sourceType === 'existing'
                ? (selectedResource?.subType || 'NA')
                : (subtypesList.find((s: any) => s.code === newResourceForm.getValues('subType'))?.name || newResourceForm.getValues('subType') || 'NA')}
            />
            <ValueLabel
              label="Serial No"
              value={sourceType === 'existing'
                ? (selectedResource?.serialNo || 'NA')
                : (newResourceForm.getValues('serialNumber') || 'NA')}
            />
            {sourceType === 'existing' && (
              <>
                <ValueLabel
                  label="Warehouse Location"
                  value={selectedResource?.warehouseName || selectedResource?.location || 'NA'}
                />
                <ValueLabel
                  label="Stock Quantity"
                  value={
                    <span className="text-emerald-600 font-semibold">
                      {selectedResource?.qty != null ? `${selectedResource.qty} units` : 'NA'}
                    </span>
                  }
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
